/**
 * Typed wrappers over the frontend-supporting REST endpoints — `health/*`, `system/*`,
 * `sources/*`, `settings`, `saved_searches`, and the maintenance actions (ADR 008 /
 * scaffolding §3 Issue 8 / frontend.md §2.4, §3.1).
 *
 * These are REST-only — NOT MCP tools — and return their payload BARE (not enveloped), so
 * they go through `callEndpoint`, not `callTool`. Routes are taken verbatim from
 * `backend/aletheia/api/transport/rest_app.py::_mount_frontend_endpoints`.
 *
 * SEAMS: several of these endpoints are wired against what the v1 api-core port set
 * exposes and return a structurally-correct but partially-empty payload with a
 * `detail.seam` note (e.g. `sources/joplin/status`, `system/cost_telemetry`,
 * `saved_searches`). The frontend renders them honestly — the surfaces show the wired
 * shape and treat an empty result as "not yet available", never as "zero". That is a
 * backend integration concern, not a frontend bug.
 */

import { callEndpoint } from './client';
import { keysToSnake } from './mappers';
import type {
  AutorouterActivityItem,
  AutorouterPreset,
  ConflictedNoteList,
  CostTelemetryReport,
  CountReport,
  CountReportName,
  ExtractionQueueStatus,
  FailedExtractionList,
  InferenceMetrics,
  JoplinSourceStatus,
  MarkdownImportRequest,
  ResolveConflictRequest,
  RetryExtractionRequest,
  SavedSearchList,
  SettingsUpdate,
  SettingsView,
  SoftDeletedNoteList,
  SoftDeletedNotebookList,
  SourceActionResult,
  SystemStatus,
} from '@/types';
import type { NoteId, NotebookChildren, NotebookId, NotebookNoteList } from '@/types';

// --- system/* ---------------------------------------------------------------
//
// `fetchCapability` removed when the local ONNX/CUDA embedding pipeline was
// retired in favour of OpenAI's hosted embeddings (2026-05-17). The frontend
// no longer needs a hardware-tier probe; render budgets are now hard-coded
// to mid-tier defaults at their call sites.

export const fetchSystemStatus = (): Promise<SystemStatus> =>
  callEndpoint('GET', 'system/status');

export const fetchCostTelemetry = (): Promise<CostTelemetryReport> =>
  callEndpoint('GET', 'system/cost_telemetry');

// --- health/* ---------------------------------------------------------------

export const fetchExtractionQueue = (): Promise<ExtractionQueueStatus> =>
  callEndpoint('GET', 'health/extraction_queue');

export const fetchInferenceMetrics = (): Promise<InferenceMetrics> =>
  callEndpoint('GET', 'health/inference_metrics');

export const fetchFailedExtractions = (
  limit: number,
  offset: number,
): Promise<FailedExtractionList> =>
  callEndpoint('GET', 'health/failed_extractions', { query: { limit, offset } });

export const fetchSoftDeleted = (
  limit: number,
  offset: number,
): Promise<SoftDeletedNoteList> =>
  callEndpoint('GET', 'health/soft_deleted', { query: { limit, offset } });

export const fetchConflictedNotes = (
  limit: number,
  offset: number,
): Promise<ConflictedNoteList> =>
  callEndpoint('GET', 'health/conflicted_notes', { query: { limit, offset } });

/**
 * The generic `{label: count}` health reports — `inferred_links`, `duplicate_clusters`,
 * `orphan_stale`, `action_item_aging`, `index_health`. v1 of several of these returns
 * only `total_live_notes` plus a `detail.seam` note (the dedicated rollups are a Stage-D
 * concern not yet exposed); the panels read whichever keys are present.
 */
export const fetchCountReport = (report: CountReportName): Promise<CountReport> =>
  callEndpoint('GET', `health/${report}`);

// --- sources/* --------------------------------------------------------------

export const fetchJoplinStatus = (): Promise<JoplinSourceStatus> =>
  callEndpoint('GET', 'sources/joplin/status');

export const triggerJoplinPoll = (): Promise<SourceActionResult> =>
  callEndpoint('POST', 'sources/joplin/poll_now');

export const triggerMarkdownImport = (
  req: MarkdownImportRequest,
): Promise<SourceActionResult> =>
  callEndpoint('POST', 'sources/markdown/import', { body: req });

// --- settings + settings/api_keys ------------------------------------------

export const fetchSettings = (): Promise<SettingsView> => callEndpoint('GET', 'settings');

/**
 * `PUT /api/v1/settings` — update the non-secret settings (ADR 008). The frontend's
 * `SettingsUpdate` is flattened into the snake_case `values`-level keys the backend
 * `PUT` expects: `owner_entity_id`, `joplin_write_back_enabled` (D-007, default off),
 * `backfill_mode`, `llm_spend_cap_usd`, `joplin_poll_interval_minutes`. API keys are
 * REJECTED here by the backend — they go through `putApiKeys`.
 */
export function putSettings(update: SettingsUpdate): Promise<SettingsView> {
  // SettingsUpdate's camelCase keys map 1:1 to the backend's snake_case settings keys.
  const body = keysToSnake<Record<string, unknown>>(update);
  return callEndpoint('PUT', 'settings', { body });
}

/**
 * `PUT /api/v1/settings/api_keys` — write-only API-key storage (ADR 008). Keys go in,
 * they never come out. The values land in the OS keychain (a backend seam); the response
 * carries only the accepted key *names*.
 */
export function putApiKeys(keys: Record<string, string>): Promise<SourceActionResult> {
  // NOTE: not run through keysToSnake — the key NAMES here are the user's provider keys
  // ('anthropic_api_key', etc.), already in the backend's expected form. Values are never
  // logged (the logger redacts; `callEndpoint` does not log bodies).
  return callEndpoint('PUT', 'settings/api_keys', { body: keys });
}

// --- autorouter (docs/design/AUTOROUTER-SPEC.md) ----------------------------
// These endpoints return the {ok, data} envelope shape as a bare dict (not via callTool),
// so we extract `.data` here. keysToCamel has already run inside callEndpoint.

interface Enveloped<T> {
  ok: boolean;
  data: T;
}

/** `GET /autorouter/presets` — the 10 shipped routing-prompt presets. */
export async function fetchAutorouterPresets(): Promise<{
  version: string;
  defaultId: string;
  presets: AutorouterPreset[];
}> {
  const res = await callEndpoint<Enveloped<{
    version: string;
    defaultId: string;
    presets: AutorouterPreset[];
  }>>('GET', 'autorouter/presets');
  return res.data;
}

/** `GET /autorouter/routed_note_ids` — the note ids currently showing the Autorouted badge. */
export async function fetchRoutedNoteIds(): Promise<string[]> {
  const res = await callEndpoint<Enveloped<{ noteIds: string[] }>>(
    'GET',
    'autorouter/routed_note_ids',
  );
  return res.data.noteIds ?? [];
}

/** `GET /autorouter/activity` — recent routing decisions for the Settings activity list. */
export async function fetchAutorouterActivity(
  limit = 50,
): Promise<AutorouterActivityItem[]> {
  const res = await callEndpoint<Enveloped<{ items: AutorouterActivityItem[] }>>(
    'GET',
    `autorouter/activity?limit=${limit}`,
  );
  return res.data.items ?? [];
}

/** `POST /autorouter/undo/{id}` — send a routed note back to the Inbox + clear the badge. */
export const undoAutorouting = (logId: string): Promise<unknown> =>
  callEndpoint('POST', `autorouter/undo/${encodeURIComponent(logId)}`);

/** `POST /notebooks/{id}/autorouter` — flip the per-folder router permission. */
export const setNotebookAutorouter = (
  notebookId: NotebookId,
  enabled: boolean,
): Promise<unknown> =>
  callEndpoint('POST', `notebooks/${encodeURIComponent(notebookId)}/autorouter`, {
    body: { enabled },
  });

// --- saved_searches ---------------------------------------------------------

export const fetchSavedSearches = (): Promise<SavedSearchList> =>
  callEndpoint('GET', 'saved_searches');

// --- notebooks --------------------------------------------------------------

/**
 * `GET /api/v1/notebooks/children?parent_id=<id>` — direct children of a notebook,
 * or the roots if `parentId` is null/omitted (UX-reconciliation F4.1).
 *
 * The Editor's `<NotebooksTreePane>` calls this lazily — once for the roots, then
 * again per parent when the user expands a node. TanStack Query keys it on the
 * parent id so each level is independently cached.
 */
export const fetchNotebookChildren = (parentId: NotebookId | null): Promise<NotebookChildren> =>
  callEndpoint(
    'GET',
    parentId === null ? 'notebooks/children' : `notebooks/children?parent_id=${encodeURIComponent(parentId)}`,
  );

/**
 * `GET /api/v1/notebooks/{id}/notes` — paginated live notes in a notebook
 * (UX-reconciliation F4.1 — editor's left-pane inline note list).
 *
 * Returns up to 200 notes per call (server default); rare notebooks larger than that
 * page through `limit` + `offset`. The frontend default request asks for the first
 * page only — extending to "load more" is a follow-up if any notebook exceeds 200.
 */
export const fetchNotesByNotebook = (
  notebookId: NotebookId,
): Promise<NotebookNoteList> =>
  callEndpoint('GET', `notebooks/${encodeURIComponent(notebookId)}/notes`);

/**
 * `GET /api/v1/notebooks/{id}/ancestors` — root-down ancestor chain of a
 * notebook (2026-05-18). Backs `<NotebooksTreePane>` auto-expand: when a
 * user clicks a search result for a note, we query this endpoint with the
 * note's notebookId, get the full chain, and expand every notebook in it
 * so the note's row becomes visible. Inclusive of the input id; empty
 * list if the notebook is soft-deleted or doesn't exist (the tree just
 * doesn't auto-expand in that case).
 */
export const fetchNotebookAncestors = (
  notebookId: NotebookId,
): Promise<{ notebookIds: NotebookId[] }> =>
  callEndpoint('GET', `notebooks/${encodeURIComponent(notebookId)}/ancestors`);

// ---------------------------------------------------------------------------
// The interactive "My Notes" table (2026-06-23) — GET /api/v1/notes
// ---------------------------------------------------------------------------

/** One row in the notes table (camelCased from the backend `NotesTableRow`). */
export interface NotesTableRowDTO {
  noteId: string;
  title: string;
  notebookId: string | null;
  source: string;
  createdAt: string;
  updatedAt: string;
  noteDate: string | null;
  starred: boolean;
  tags: string[];
}

/** A page of notes-table rows + the total matching the filters. */
export interface NotesTablePageDTO {
  items: NotesTableRowDTO[];
  total: number;
  limit: number;
  offset: number;
}

export type NotesTableOrderBy = 'title' | 'created' | 'updated';
export type NotesTableDirection = 'asc' | 'desc';

export interface NotesTableParams {
  orderBy?: NotesTableOrderBy;
  direction?: NotesTableDirection;
  /** Title substring search (case-insensitive). */
  q?: string;
  notebookIds?: string[];
  /** ltree tag paths; subtree match. */
  tags?: string[];
  sources?: string[];
  /** ISO date (YYYY-MM-DD). */
  dateFrom?: string;
  dateTo?: string;
  limit?: number;
  offset?: number;
}

/**
 * `GET /api/v1/notes` — a sortable/filterable/paginated page of notes for the My Notes table.
 * Server-side sort + filter + pagination (required at 100K-note scale). Array filters serialize
 * as repeated query params (`?tag=a&tag=b`).
 */
export function fetchNotesTable(p: NotesTableParams): Promise<NotesTablePageDTO> {
  return callEndpoint<NotesTablePageDTO>('GET', 'notes', {
    query: {
      order_by: p.orderBy,
      direction: p.direction,
      q: p.q && p.q.length > 0 ? p.q : undefined,
      notebook_id: p.notebookIds && p.notebookIds.length > 0 ? p.notebookIds : undefined,
      tag: p.tags && p.tags.length > 0 ? p.tags : undefined,
      source: p.sources && p.sources.length > 0 ? p.sources : undefined,
      date_from: p.dateFrom,
      date_to: p.dateTo,
      limit: p.limit,
      offset: p.offset,
    },
  });
}

/**
 * Phase E.2 (2026-05-20) — user-authored notebook create.
 * POST /api/v1/notebooks
 */
export const createNotebook = (
  title: string,
  parentId: NotebookId | null,
): Promise<{ id: NotebookId; title: string; parentId: NotebookId | null; hasChildren: boolean; noteCount: number }> =>
  callEndpoint('POST', 'notebooks', {
    body: { title, parent_id: parentId },
  });

/**
 * Phase E.2 — rename a notebook.
 * PATCH /api/v1/notebooks/{id}
 */
export const renameNotebook = (
  notebookId: NotebookId,
  title: string,
): Promise<{ id: NotebookId; title: string; parentId: NotebookId | null; hasChildren: boolean; noteCount: number }> =>
  callEndpoint('PATCH', `notebooks/${encodeURIComponent(notebookId)}`, {
    body: { title },
  });

/**
 * Drag-and-drop reparent — move a notebook under a new parent (`null` = top level).
 * PATCH /api/v1/notebooks/{id}/parent. The backend rejects cycles + the protected Inbox.
 */
export const reparentNotebook = (
  notebookId: NotebookId,
  parentId: NotebookId | null,
): Promise<{ id: NotebookId; title: string; parentId: NotebookId | null; hasChildren: boolean; noteCount: number }> =>
  callEndpoint('PATCH', `notebooks/${encodeURIComponent(notebookId)}/parent`, {
    body: { parent_id: parentId },
  });

/**
 * Phase E.2 — soft-delete a notebook.
 * DELETE /api/v1/notebooks/{id}
 *
 * Notes inside are preserved (notebook_id stays intact for recovery).
 * Idempotent — re-deleting an already-deleted notebook succeeds.
 */
export const deleteNotebook = (
  notebookId: NotebookId,
): Promise<{ accepted: boolean; message: string }> =>
  callEndpoint('DELETE', `notebooks/${encodeURIComponent(notebookId)}`);

// --- maintenance actions ----------------------------------------------------

export function retryExtraction(req: RetryExtractionRequest): Promise<SourceActionResult> {
  return callEndpoint('POST', 'extraction/retry', {
    body: { job_id: req.jobId, force_components: req.forceComponents },
  });
}

/**
 * `POST /api/v1/extraction/reextract` — bulk re-enqueue notes for a specific extraction
 * stage + components (Phase 0/3 of the 2026-05-18 extraction-remediation plan).
 *
 * Distinct from `retryExtraction` (which targets a specific failed job by id and clears
 * its error). This endpoint takes a list of note ids and enqueues a fresh job per note
 * with the requested `stage` ('A1' or 'C') and optional `components` (forced — runs even
 * when their version is unchanged, REVIEW S5 selective re-extraction).
 *
 * The editor toolbar's "Reprocess" button calls this with `note_ids: [currentNoteId]`,
 * `stage: 'C'`, `components: ['hashtags', 'action_items']` to redrive the LLM passes for
 * the currently-open note after a pasted-content cleanup or alias merge.
 */
export interface ReextractRequest {
  noteIds: NoteId[];
  stage: 'A1' | 'C';
  components?: string[];
  priority?: number;
}

export function reextractNotes(req: ReextractRequest): Promise<SourceActionResult> {
  return callEndpoint('POST', 'extraction/reextract', {
    body: {
      note_ids: req.noteIds,
      stage: req.stage,
      components: req.components ?? [],
      priority: req.priority ?? 50,
    },
  });
}

export function restoreNote(noteId: NoteId): Promise<SourceActionResult> {
  return callEndpoint('POST', `notes/${noteId}/restore`);
}

/**
 * `DELETE /api/v1/notes/{id}/permanent` — Trash UX hard-delete (friction-log fix #90,
 * 2026-05-29). The note must ALREADY be soft-deleted; the backend refuses with
 * `validation_error` on a live note (defense-in-depth — the UI only exposes this from
 * inside the Trash folder, where everything is soft-deleted by construction). FK cascades
 * clean up chunks, companion, edges, note_tags, action_items in one transaction. No
 * recovery after this.
 */
export function hardDeleteNote(noteId: NoteId): Promise<SourceActionResult> {
  return callEndpoint('DELETE', `notes/${encodeURIComponent(noteId)}/permanent`);
}

/**
 * `POST /api/v1/notes/empty_trash` — Empty Trash (friction-log fix #90, 2026-05-29).
 * Permanently deletes EVERY soft-deleted note in the workspace in one statement.
 * Backend returns the count in `detail.removed_count`. The UI is expected to confirm
 * before calling — no server-side confirmation modal.
 */
export function emptyTrash(): Promise<SourceActionResult> {
  return callEndpoint('POST', 'notes/empty_trash');
}

/**
 * `GET /api/v1/health/soft_deleted_notebooks` — list soft-deleted notebooks for the Trash
 * recovery view (#95, 2026-05-29). Each entry carries the deletion timestamp + a count of
 * notes that still belong to that notebook (they reappear on restore).
 */
export const fetchSoftDeletedNotebooks = (): Promise<SoftDeletedNotebookList> =>
  callEndpoint('GET', 'health/soft_deleted_notebooks');

/**
 * `POST /api/v1/notebooks/{id}/restore` — restore a soft-deleted notebook. Notes inside
 * come back automatically (their `notebook_id` was preserved during soft-delete).
 */
export function restoreNotebook(notebookId: NotebookId): Promise<SourceActionResult> {
  return callEndpoint('POST', `notebooks/${encodeURIComponent(notebookId)}/restore`);
}

/**
 * `POST /api/v1/extraction/pause` / `/resume` — master switch for the background worker pool.
 * Persists the choice in settings AND stops/starts the pool in-process. Used by the Settings
 * "Background extraction" toggle so the user can drop the backend to a near-zero-RAM idle.
 */
export function pauseExtraction(): Promise<SourceActionResult> {
  return callEndpoint('POST', 'extraction/pause');
}
export function resumeExtraction(): Promise<SourceActionResult> {
  return callEndpoint('POST', 'extraction/resume');
}

export function resolveConflict(req: ResolveConflictRequest): Promise<SourceActionResult> {
  return callEndpoint('POST', `notes/${req.noteId}/resolve_conflict`, {
    body: { resolution: req.resolution },
  });
}

// --- /api/v1/themes — user-authored UI themes (2026-06-01) -----------------

import type { CustomTheme, CustomThemeList } from '@/types/wire';

/** Snake-cased wire row (what the backend emits). */
interface CustomThemeWireRow {
  id: string;
  workspace_id: string;
  name: string;
  seed_theme_id: string;
  tokens: Record<string, string>;
  row_created_at: string;
  row_updated_at: string;
}

interface ThemeEnvelope<T> {
  ok: boolean;
  data: T;
}

function mapCustomTheme(row: CustomThemeWireRow): CustomTheme {
  return {
    id: row.id,
    workspaceId: row.workspace_id,
    name: row.name,
    seedThemeId: row.seed_theme_id,
    tokens: { ...row.tokens },
    rowCreatedAt: row.row_created_at,
    rowUpdatedAt: row.row_updated_at,
  };
}

/** `GET /api/v1/themes` — every custom theme in the caller's workspace. */
export async function listCustomThemes(): Promise<CustomThemeList> {
  const resp = await callEndpoint<ThemeEnvelope<{ themes: CustomThemeWireRow[] }>>(
    'GET',
    'themes',
  );
  return { themes: resp.data.themes.map(mapCustomTheme) };
}

export interface CreateCustomThemeRequest {
  name: string;
  seedThemeId: string;
  tokens: Record<string, string>;
}

/** `POST /api/v1/themes` — create a new custom theme. */
export async function createCustomTheme(
  req: CreateCustomThemeRequest,
): Promise<CustomTheme> {
  const resp = await callEndpoint<ThemeEnvelope<CustomThemeWireRow>>('POST', 'themes', {
    body: {
      name: req.name,
      seed_theme_id: req.seedThemeId,
      tokens: req.tokens,
    },
  });
  return mapCustomTheme(resp.data);
}

export interface UpdateCustomThemeRequest {
  themeId: string;
  name?: string;
  tokens?: Record<string, string>;
}

/** `PATCH /api/v1/themes/{id}` — update a custom theme's name and/or tokens. */
export async function updateCustomTheme(
  req: UpdateCustomThemeRequest,
): Promise<CustomTheme> {
  const body: Record<string, unknown> = {};
  if (req.name !== undefined) body.name = req.name;
  if (req.tokens !== undefined) body.tokens = req.tokens;
  const resp = await callEndpoint<ThemeEnvelope<CustomThemeWireRow>>(
    'PATCH',
    `themes/${encodeURIComponent(req.themeId)}`,
    { body },
  );
  return mapCustomTheme(resp.data);
}

/** `DELETE /api/v1/themes/{id}` — hard-delete a custom theme. */
export async function deleteCustomTheme(themeId: string): Promise<void> {
  await callEndpoint('DELETE', `themes/${encodeURIComponent(themeId)}`);
}
